import {
  SubmitPushToCardTransferErrorAction,
  TransferRail,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useRouter,
} from 'expo-router';
import {
  pressableAttributes,
  type PressableComponentType,
} from 'features/feeBasedInstantTransfer/helpers/analyticsFormatters';
import useMatchedSubmitOutcome from 'features/feeBasedInstantTransfer/hooks/useMatchedSubmitOutcome';
import { submitErrorAtom } from 'features/feeBasedInstantTransfer/state/submitOutcomeAtoms';
import { useTranslation } from 'react-i18next';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import {
  ErrorScreen,
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';

const TRANSFER_ERROR_SCREEN_KEY = 'instantTransferFlowError';
const SUBMIT_STEP_NAME = 'submit';
const MAX_ACTION_BUTTONS = 2;
const ENTRY_PATHNAME = '/move-money/transfer/instant-transfer';

type ServerAction = Exclude<
  SubmitPushToCardTransferErrorAction,
  SubmitPushToCardTransferErrorAction.Undefined
>;

const SERVER_ACTIONS: readonly SubmitPushToCardTransferErrorAction[] = [
  SubmitPushToCardTransferErrorAction.Retry,
  SubmitPushToCardTransferErrorAction.Restart,
  SubmitPushToCardTransferErrorAction.Dismiss,
];

const isServerAction = (
  value: SubmitPushToCardTransferErrorAction,
): value is ServerAction => SERVER_ACTIONS.includes(value);

// The header close button already dismisses, so DISMISS yields its slot when the server offers more actions than fit.
const toButtonActions = (
  serverActions: SubmitPushToCardTransferErrorAction[],
) => {
  const actions = [...new Set(serverActions.filter(isServerAction))];
  const fittingActions =
    actions.length > MAX_ACTION_BUTTONS
      ? actions.filter(
          (action) => action !== SubmitPushToCardTransferErrorAction.Dismiss,
        )
      : actions;
  return fittingActions.slice(0, MAX_ACTION_BUTTONS);
};

type ButtonSlot = Exclude<PressableComponentType, 'dialog_action'>;

function TransferErrorScreen() {
  const { t } = useTranslation('feeBasedInstantTransfer');
  const router = useRouter();
  const { defaultTitle, defaultMessage, doneButtonTitle, retryButtonTitle } =
    useDefaultErrorStrings();
  const submitError = useMatchedSubmitOutcome(submitErrorAtom);

  if (!submitError) {
    return <Redirect href="/" />;
  }

  const { rail, errorReason, reviewed, serverError } = submitError;
  const { source, destination, amount, currency } = reviewed;
  const entryParams = { fromId: source.id, amount };
  const errorAttributes = {
    step_name: SUBMIT_STEP_NAME,
    error_reason: errorReason,
  };
  const isDebitCardTransfer = rail === TransferRail.PushToCard;

  const fallbackTitle = isDebitCardTransfer ? t('error.title') : defaultTitle;
  const fallbackMessage = isDebitCardTransfer
    ? t('error.debitCardMessage')
    : defaultMessage;
  const [primaryAction, secondaryAction] = toButtonActions(
    serverError?.actions ?? [],
  );

  const handleChooseDifferentAccount = () => {
    // The entry reads its preselection once on mount, so it must remount to drop an Advance lock.
    router.replace({ pathname: ENTRY_PATHNAME, params: entryParams });
  };

  const handleRetry = () => {
    router.dismissTo({ pathname: ENTRY_PATHNAME, params: entryParams });
    router.push({
      pathname: '/move-money/transfer/review-transfer-details',
      params: {
        sourceAccountId: source.id,
        destinationId: destination.id,
        destinationType: destination.type,
        amount,
        currency,
      },
    });
  };

  const handleRestart = () => {
    router.replace(ENTRY_PATHNAME);
  };

  const actionButtons = {
    [SubmitPushToCardTransferErrorAction.Retry]: {
      title: retryButtonTitle,
      pressableName: 'Try Again',
      analyticsId: 'transferMoneyErrorRetry',
      onPress: handleRetry,
    },
    [SubmitPushToCardTransferErrorAction.Restart]: {
      title: t('error.chooseDifferentAccount'),
      pressableName: 'Choose A Different Account',
      analyticsId: 'transferMoneyErrorRestart',
      onPress: handleRestart,
    },
    [SubmitPushToCardTransferErrorAction.Dismiss]: {
      title: doneButtonTitle,
      pressableName: 'Done',
      analyticsId: 'transferMoneyErrorDone',
      onPress: navigateBack,
    },
  } satisfies Record<
    ServerAction,
    {
      title: string;
      pressableName: string;
      analyticsId: string;
      onPress: () => void;
    }
  >;

  const actionButton = (action: ServerAction | undefined, slot: ButtonSlot) => {
    if (!action) {
      return undefined;
    }
    const {
      title: buttonTitle,
      pressableName,
      analyticsId,
      onPress,
    } = actionButtons[action];
    return {
      title: buttonTitle,
      onPress,
      analytics: {
        analyticsId,
        customAttributes: pressableAttributes(
          pressableName,
          slot,
          errorAttributes,
        ),
      },
    };
  };

  const chooseDifferentAccountButton = {
    title: t('error.chooseDifferentAccount'),
    onPress: handleChooseDifferentAccount,
    analytics: {
      analyticsId: 'transferMoneyErrorChooseDifferentAccount',
      customAttributes: pressableAttributes(
        'Choose A Different Account',
        'primary',
        errorAttributes,
      ),
    },
  };

  return (
    <>
      <Stack.Screen
        options={{
          headerShown: true,
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={navigateBack}
              primaryIcon={
                <Icon
                  iconKey="CLOSE"
                  iconSize="MD"
                  iconAccessibilityLabel={t('error.close')}
                />
              }
            />
          ),
        }}
      />
      <ErrorScreen
        screenType={ErrorScreenType.Error}
        screenAnalyticsEventKey={TRANSFER_ERROR_SCREEN_KEY}
        messageHeadline={serverError?.title || fallbackTitle}
        messageBody={serverError?.message || fallbackMessage}
        primaryButton={
          primaryAction
            ? actionButton(primaryAction, 'primary')
            : chooseDifferentAccountButton
        }
        secondaryButton={
          primaryAction
            ? actionButton(secondaryAction, 'secondary')
            : actionButton(
                SubmitPushToCardTransferErrorAction.Dismiss,
                'secondary',
              )
        }
      />
    </>
  );
}

export default TransferErrorScreen;
